import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

export const NewFiltersModal = ({
  showModal = false,
  setShowModal,
  count,
  onClearAll,
  children,
  className = '',
  extraClasses,
  theme,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  useEffect(() => {
    if (showModal) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 700);

      return () => clearTimeout(timer);
    }
  }, [showModal]);
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };
    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showModal, setShowModal]);
  if (!shouldRender) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={`backdrop ${showModal ? 'show' : ''} ${className} ${
          extraClasses || ''
        }`}
        onClick={() => setShowModal(false)}
      />
      {/* Modal */}
      <div
        className={`modals ${showModal ? 'modal-open' : 'modal-closed'} ${
          extraClasses || ''
        }`}
      >
        <div className="modal-container">
          {/* Header */}
          <div className="modal-header">
            <h2 className="modal-title">Filters</h2>
            <button
              aria-label="Close filters"
              className="close-button"
              onClick={() => setShowModal(false)}
              type="button"
            >
              <FontAwesomeIcon className="close-icon" icon={faTimes} />
            </button>
          </div>
          {/* Content - FilterOptions goes here */}
          <div className="modal-content">{children}</div>
          {/* Footer - Only show if count and onClearAll are provided */}
          {count !== undefined && onClearAll && (
            <div className="modals-footer">
              <div className="footer-buttons">
                <span className="clear-button" onClick={onClearAll}>
                  Clear filters
                </span>
                <button
                  className="apply-button"
                  onClick={() => setShowModal(false)}
                  type="button"
                >
                  View {count} result{count !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        @keyframes backdropFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes backdropFadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
            opacity: 1;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOutToRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 1;
          }
        }
        .backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 2001;
          isolation: isolate;
        }
        .backdrop.show {
          animation: backdropFadeIn;
          animation-duration: 0.5s;
          animation-timing-function: ease-in-out;
          animation-fill-mode: forwards;
        }
        .backdrop:not(.show) {
          animation: backdropFadeOut;
          animation-duration: 0.5s;
          animation-timing-function: ease-in-out;
          animation-fill-mode: forwards;
        }
        .modals {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          height: 100%;
          overflow: hidden;
          background-color: white;
          z-index: 2002;
          transform: translateX(100%);
          isolation: isolate;
          will-change: transform;
        }
        .modal-open {
          animation: slideInFromRight;
          animation-duration: 0.7s;
          animation-timing-function: ease-in-out;
          animation-fill-mode: forwards;
        }
        .modal-closed {
          animation: slideOutToRight;
          animation-duration: 0.7s;
          animation-timing-function: ease-in-out;
          animation-fill-mode: forwards;
        }
        .modal-container {
          display: flex;
          flex-direction: column;
          height: 100dvh;
          max-height: calc(100vh - 20px);
          width: 100%;
          position: relative;
          z-index: 2003;
        }
        .modal-header {
          position: sticky;
          top: 0;
          z-index: 10;
          background: white;
          padding: 16px 24px;
          border-bottom: 1px solid #e5e7eb;
        }
        .modals-footer {
          position: fixed;
          bottom: 0;
          background: white;
          padding: 16px 24px max(24px, env(safe-area-inset-bottom, 0px)) 24px;
          border-top: 1px solid #e5e7eb;
          z-index: 2004;
        }
        .modal-title {
          font-size: 20px;
          font-weight: 600;
          color: ${theme.colors.primary};
          margin: 0;
          padding: 5px 0;
        }
        .close-button {
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          font-size: 25px;
          color: ${theme.colors.primary};
          line-height: 1;
        }
        .modal-content {
          flex: 1;
          overflow-y: auto;
          background-color: white;
          min-height: 0;
          position: relative;
        }
        .modal.no-body-padding .modal-content {
          padding: 0;
        }
        .modals-footer {
          border-top: 1px solid #e5e7eb;
          padding: 1rem 1.5rem;
          background-color: white;
          flex-shrink: 0;
          position: relative;
          z-index: 1000;
        }
        .footer-buttons {
          display: flex;
          gap: 5px;
          padding: 0 5px;
        }
        .clear-button {
          flex: 1;
          color: ${theme.colors.primary};
          font-weight: 700;
          text-decoration: underline;
          cursor: pointer;
        }
        .apply-button {
          flex: 1;
          background-color: ${theme.colors.primary};
          color: white;
          border: none;
          font-weight: 500;
          padding: 0.75rem 1rem;
          border-radius: 13px;
          transition: background-color 0.2s;
          cursor: cursor;
        }
        @media (max-width: ${theme.breakpoints.mdUp}) {
          .modals {
            left: 50px;
            will-change: transform;
          }
          .modal-header {
            padding-top: max(16px, env(safe-area-inset-top));
          }
          .modals-footer {
            padding-bottom: max(16px, env(safe-area-inset-bottom));
          }
          .clear-button,
          .apply-button {
            padding: 10px 10px;
            font-size: 14px;
          }
        }
      `}</style>
    </>
  );

  return typeof window !== 'undefined'
    ? ReactDOM.createPortal(modalContent, document.body)
    : null;
};
export default NewFiltersModal;
